import React, { useCallback, useContext, useState, useEffect, useRef } from "react";
import ManateeModal from "../components/Modal";
import NotificationBanner from "../components/NotificationBanner";
import { addEventListener } from "../events/Events";

export const GlobalComponentContext = React.createContext();

export function GlobalComponentContextProvider({ children }) {
  const [errorMessage, setErrorMessage] = useState();
  const [retryBanner, setRetryBanner] = useState({ show: false, count: 0, operationName: null });
  const [networkErrorBanner, setNetworkErrorBanner] = useState({ show: false, message: null });

  const showError = useCallback((message = "Something went wrong - please try again :(") => {
    setErrorMessage(message);
  }, []);

  const hideRetryBannerTimeoutRef = useRef(null);

  const dismissRetryBanner = useCallback(() => {
    setRetryBanner({
      show: false,
      count: 0,
      operationName: null
    });
  }, []);

  const dismissNetworkErrorBanner = useCallback(() => {
    setNetworkErrorBanner({
      show: false,
      message: null
    });
  }, []);

  useEffect(() => {
    const retrySubscription = addEventListener("queryRetry", event => {
      clearTimeout(hideRetryBannerTimeoutRef.current);
      setRetryBanner(prev => ({
        show: true,
        count: prev.count + 1,
        operationName: event.data?.operationName || null
      }));

      hideRetryBannerTimeoutRef.current = setTimeout(() => {
        setRetryBanner(prev => ({
          ...prev,
          show: false
        }));
      }, 5000);
    });

    const networkErrorSubscription = addEventListener("networkError", event => {
      setNetworkErrorBanner({
        show: true,
        message: event.data?.message || "Network connection issue"
      });

      setTimeout(() => {
        setNetworkErrorBanner(prev => ({
          ...prev,
          show: false
        }));
      }, 15000);
    });

    return () => {
      retrySubscription.remove();
      networkErrorSubscription.remove();
    };
  }, []);

  return (
    <GlobalComponentContext.Provider value={{ showError, dismissRetryBanner }}>
      {!!errorMessage && (
        <ManateeModal
          type="error"
          title={errorMessage}
          onButton1Press={() => {
            setErrorMessage(undefined);
          }}
          style={{
            zIndex: 99999,
            position: "fixed"
          }}
        />
      )}
      <NotificationBanner
        show={retryBanner.show}
        message={`Connection issue detected. Retrying... (${retryBanner.count} attempt${
          retryBanner.count !== 1 ? "s" : ""
        })${
          retryBanner.operationName && process.env.NODE_ENV !== "production" ? ` (${retryBanner.operationName})` : ""
        }`}
        onDismiss={dismissRetryBanner}
        backgroundColor="#ff9800"
        icon="⚠️"
      />
      <NotificationBanner
        show={networkErrorBanner.show && !retryBanner.show}
        message={`Network error occurred. Please check your internet connection and try again.${
          networkErrorBanner.message && process.env.NODE_ENV !== "production" ? ` (${networkErrorBanner.message})` : ""
        }`}
        onDismiss={dismissNetworkErrorBanner}
        backgroundColor="#f44336"
        icon="🌐"
      />
      {children}
    </GlobalComponentContext.Provider>
  );
}

export function useGlobalContext() {
  return useContext(GlobalComponentContext);
}
